import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { ApiResult } from '@shared/Interfaces/Api/ApiResult';
import { LoginResponse } from '../../Processes/Gateways/Login/Interfaces/LoginResponse';
import { LoginGateway } from '../../Processes/Gateways/Login/LoginGateway';
import { SESSION_STORAGE_KEYS } from '@configs/SessionStorageKeys';
import { GetHostEmail } from '@shared/Helpers/CBGetAttributes/GetHostEmail/GetHostEmail';
import { IsValidEmail } from '@shared/Helpers/AuthorizationUtils/IsValidEmail/IsValidEmail';

const loginGateway = new LoginGateway();

interface State {
    bearerToken: string | null;
    authState: boolean;
    lastAuthorizedPath: string | null;

    isAuthorizationLoading: boolean;

    fetchLogin: (email: string) => Promise<ApiResult<LoginResponse>>;
    getToken: () => Promise<string | null>;
    removeToken: () => void;
    setLastAuthorizedPath: (path: string | null) => void;
    setAuthState: (state: boolean) => void;
}

export const useAuthorizationViewModel = create<State>()(
    persist(
        (set, get) => ({
            bearerToken: null,
            authState: false,
            lastAuthorizedPath: null,

            isAuthorizationLoading: false,

            fetchLogin: async (email: string) => {
                set({ isAuthorizationLoading: true });

                const response = await loginGateway.login(email);

                set({
                    isAuthorizationLoading: false,
                    bearerToken: response.data?.token,
                });
                return response;
            },

            getToken: async () => {
                const { bearerToken, fetchLogin } = get();

                if (bearerToken && bearerToken !== '') {
                    return bearerToken;
                }

                const email = GetHostEmail();
                const isValidEmail = IsValidEmail(email);

                if (!isValidEmail || !email) {
                    if (bearerToken) {
                        set({ bearerToken: null });
                    }
                    return null;
                }

                const response = await fetchLogin(email);
                return response.data ? response.data.token : null;
            },

            removeToken: () => {
                set({ bearerToken: null });
            },

            setLastAuthorizedPath: (path: string | null) => {
                set({ lastAuthorizedPath: path });
            },

            setAuthState: (state: boolean) => {
                set({ authState: state });
            },
        }),

        {
            name: SESSION_STORAGE_KEYS.BEARER,
            storage: createJSONStorage(() => sessionStorage),
            partialize: state => ({
                bearerToken: state.bearerToken,
                authState: state.authState,
                lastAuthorizedPath: state.lastAuthorizedPath,
            }),
        }
    )
);
